import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

export interface AdminStrategy {
  id: string;
  name: string;
  description: string | null;
  playbook_ids: string[];
  daily_budget_usd: string;
  max_position_size_pct: string;
  cooldown_after_losing_trade_hours: number;
  live_confidence_threshold: string;
  live_enabled: boolean;
  paused: boolean;
  created_at: string;
  updated_at: string;
  version: number;
}

export interface CreateStrategyBody {
  name: string;
  description?: string | null;
  playbook_ids: string[];
  daily_budget_usd: number;
  max_position_size_pct: number;
  cooldown_after_losing_trade_hours: number;
  live_confidence_threshold: number;
  live_enabled: boolean;
}

export interface UpdateStrategyBody {
  name?: string;
  description?: string | null;
  playbook_ids?: string[];
  daily_budget_usd?: number;
  max_position_size_pct?: number;
  cooldown_after_losing_trade_hours?: number;
  live_confidence_threshold?: number;
  live_enabled?: boolean;
}

export const adminStrategiesKey = ["admin", "strategies"] as const;

export function useAdminStrategies() {
  return useQuery<AdminStrategy[]>({
    queryKey: adminStrategiesKey,
    queryFn: () => apiFetch<AdminStrategy[]>("/admin/strategies"),
    staleTime: 30_000,
  });
}

export function useCreateStrategy() {
  const qc = useQueryClient();
  return useMutation<AdminStrategy, Error, CreateStrategyBody>({
    mutationFn: (body) =>
      apiFetch<AdminStrategy>("/admin/strategies", {
        method: "POST",
        body,
      }),
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: adminStrategiesKey });
    },
  });
}

export function useUpdateStrategy(id: string) {
  const qc = useQueryClient();
  return useMutation<AdminStrategy, Error, UpdateStrategyBody>({
    mutationFn: (body) =>
      apiFetch<AdminStrategy>(`/admin/strategies/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: (updated) => {
      qc.setQueryData<AdminStrategy[]>(adminStrategiesKey, (prev) => {
        if (!prev) return prev;
        return prev.map((s) => (s.id === id ? updated : s));
      });
    },
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: adminStrategiesKey });
    },
  });
}

export function useDeleteStrategy() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiFetch<void>(`/admin/strategies/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: adminStrategiesKey });
    },
  });
}
